import { OverlayModule } from '@angular/cdk/overlay';
import { PortalModule } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, NG_VALIDATORS, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTab, MatTabGroup, MatTabLabel } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { FaConfig, FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faArrowPointer, faArrowsUpDownLeftRight, faClockRotateLeft, faDrawPolygon, faDrawSquare, faEnvelope, faFile, faFileDownload, faFileUpload, faFloppyDisk, faHandPointer, faLayerGroup, faLayerMinus, faLayerPlus, faMap, faPenNib, faPhone, faRotateLeft as falRotateLeft, faSignsPost, faSort } from '@fortawesome/pro-light-svg-icons';
import { faArrowLeft, faArrowsRotate, faArrowUpRightFromSquare as farArrowUpRightFromSquare, faBadgeDollar, faCircleCheck, faCircleInfo, faCircleQuestion, faClone, faCrosshairs, faDownload, faEdit as farEdit, faEye as farEye, faEyeSlash as farEyeSlash, faFilter, faFlag, faLock, faMap as farMap, faPaperclip, faPencil, faShare, faSquareDashed, faSquareDashedCirclePlus, faTrash, faTrashUndo, faTree as farTree, faTreeDeciduous as farTreeDeciduous, faTriangleExclamation, faUpload, faUserShield, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faArrowUpRightFromSquare, faBars, faBorderAll, faCaretDown, faCaretUp, faChartSimple, faCheck, faCheckCircle, faChevronLeft, faChevronRight, faChevronsDown, faChevronsLeft, faChevronsRight, faChevronsUp, faCircle, faCircleCheck as fasCircleCheck, faCircleExclamation, faCircleMinus, faCirclePlus, faCircleUser, faCircleXmark, faEdit, faExpandWide, faEye, faEyeSlash, faGauge, faMap as faSolidMap, faMinus, faMoneyCheckDollar, faOctagon, faPaperPlane, faPenToSquare, faPlus, faQuestion, faQuestionCircle, faRoad, faRotateLeft, faShieldHalved, faSortDown, faSortUp, faStar, faTableColumns, faTree, faTreeDeciduous, faTriangle, faWater, } from '@fortawesome/pro-solid-svg-icons';
import { facCustomTree, faDownloadMap, faUploadMap } from './icons/custom-icons';
import { NgxChartsModule } from '@swimlane/ngx-charts';

import { AppHeaderComponent } from './app-header/app-header.component';
import { UserActivityDashboardComponent } from './components/activity-dashboard/user-activity-dashboard.component';
import { DashboardBannerComponent } from './components/dashboard-banner/dashboard-banner.component';
import { DialogChangeSiteStateComponent } from './components/dialog-change-site-state/dialog-change-site-state.component';
import { DisturbanceReportComponent } from './components/disturbance-report/disturbance-report.component';
import { EnvRibbonComponent } from './components/env-ribbon/env-ribbon.component';
import { EmailFormErrorComponent } from './components/form-errors/email-form-error.component';
import { FCControlComponent } from './components/forms/fc-control.component';
import { FCDatepickerComponent } from './components/forms/fc-datepicker.component';
import { FCSortIconComponent } from './components/icons/sort-icon.component';
import { LegalPageComponent } from './components/legal-page/legal-page.component';
import { NewSiteStepOneComponent } from './components/new-site-step-one/new-site-step-one.component';
import { PeriodicReportComponent } from './components/periodic-report/periodic-report.component';
import { ProfilePageComponent } from './components/profile-page/profile-page.component';
import { ProgramListComponent } from './components/program-list/program-list.component';
import { ProgressBarComponent } from './components/progress-bar/progress-bar.component';
import { ReportFormComponent } from './components/report-form/report-form.component';
import { ReportResponseComponent } from './components/report-response/report-response.component';
import { SiteCardComponent } from './components/site-card/site-card.component';
import { SiteDetailInformationComponent } from './components/site-detail-information/site-detail-information.component';
import { SiteDetailNotesComponent } from './components/site-detail-notes/site-detail-notes.component';
import { SiteDetailOverviewComponent } from './components/site-detail-overview/site-detail-overview.component';
import { SiteDetailPaymentsComponent } from './components/site-detail-payments/site-detail-payments.component';
import { SiteDetailReportsComponent } from './components/site-detail-reports/site-detail-reports.component';
import { SiteDocumentListComponent } from './components/site-document-list/site-document-list.component';
import { SiteVerificationVisitsComponent } from './components/site-verification-visits/site-verification-visits.component';
import { TablePaginationComponent } from './components/table-pagination/table-pagination.component';
import { ToastComponent } from './components/toast/toast.component';
import { VerifyAccountComponent } from './components/verify-account-page/verify-account.component';
import { AddDocusignDialogComponent } from './dialogs/add-docusign-dialog/add-docusign-dialog.component';
import { AddUserDialogComponent } from './dialogs/add-user-dialog/add-user-dialog.component';
import { AdjustValueDialogComponent } from './dialogs/adjust-value-dialog/adjust-value-dialog.component';
import { DialogChangeActingAsUserComponent } from './dialogs/dialog-change-acting-as-user/dialog-change-acting-as-user.component';
import { DialogChangeApplicantComponent } from './dialogs/dialog-change-owner/dialog-change-applicant.component';
import { DialogComingSoonComponent } from './dialogs/dialog-coming-soon/dialog-coming-soon.component';
import { DialogConfirmPaymentComponent } from './dialogs/dialog-confirm-payment/dialog-confirm-payment.component';
import { DialogDownForMaintenanceComponent } from './dialogs/dialog-down-for-maintenance/dialog-down-for-maintenance.component';
import { DialogEditPaymentPeriodComponent } from './dialogs/dialog-edit-payment-period/dialog-edit-payment-period.component';
import { DialogEditSiteNoteComponent } from './dialogs/dialog-edit-site-note/dialog-edit-site-note.component';
import { DialogEligibilityQuestionComponent } from './dialogs/dialog-eligibility-question/dialog-eligibility-question.component';
import { DialogImportKmlComponent } from './dialogs/dialog-import-kml/dialog-import-kml.component';
import { DialogManualIDComponent } from './dialogs/dialog-manual-id/dialog-manual-id.component';
import { DialogOutOfRegionComponent } from './dialogs/dialog-out-of-region/dialog-out-of-region.component';
import { DialogResubmitNoteComponent } from './dialogs/dialog-resubmit-note/dialog-resubmit-note.component';
import { DialogSendEdocComponent } from './dialogs/dialog-send-edoc/dialog-send-edoc.component';
import { DialogSplitSiteComponent } from './dialogs/dialog-split-site/dialog-split-site.component';
import { DialogUploadDocComponent } from './dialogs/dialog-upload-doc/dialog-upload-doc.component';
import { DialogYesNoComponent } from './dialogs/dialog-yes-no/dialog-yes-no.component';
import { OtpDialogComponent } from './dialogs/otp-dialog/otp-dialog.component';
import { ClickDirective } from './directives/click.directive';
import { CurrencyMaskDirective } from './directives/currency-mask-directive';
import { DateMaskDirective } from './directives/date-mask.directive';
import { DragDropUploadDirective } from './directives/drag-drop-upload-directive.directive';
import { FormUnloadWarningDirective } from './directives/form-unload-warning.directive';
import { RegexFilterDirective } from './directives/regex-filter.directive';
import { SpinnerComponent } from './spinner/spinner.component';
import { MatPaginatorModule } from "@angular/material/paginator";
import { DialogEditCountyDataComponent } from "./dialogs/dialog-edit-county-data/dialog-edit-county-data.component";
import { DragDropModule } from '@angular/cdk/drag-drop';
import { DialogChangeDashboardNameComponent } from "./dialogs/dialog-change-dashboard-name/dialog-change-dashboard-name.component";
import { AppFooterComponent } from "./components/app-footer/app-footer.component";
import { DialogSelectOptionComponent } from "./dialogs/dialog-select-option/dialog-select-option.component";
import { ConfirmRemovalDialogComponent } from "./dialogs/confirm-removal-dialog/confirm-removal-dialog.component";
import { DialogRetainSubmitDateComponent } from "./dialogs/dialog-retain-submit-date/dialog-retain-submit-date.component";
import { DialogEditReportingPeriodComponent } from "./dialogs/dialog-edit-reporting-period/dialog-edit-reporting-period.component";
import { DialogEditCustomOfferComponent } from "./dialogs/dialog-edit-custom-offer/dialog-edit-custom-offer.component";
import { HtmlTooltipDirective } from '@shared/directives/html-tooltip.directive';
import { PaginatorLabelDirective } from '@shared/directives/paginator-label.directive';
import { StringDiffComponent } from '@shared/components/string-diff/string-diff.component';
import { DisplayTableFilterComponent } from '@shared/components/display-table-filter/display-table-filter.component';
import { DisplayTableBaseComponent } from '@shared/components/display-table-base-new/display-table-base.component';
import { ColumnTransformPipe } from '@shared/pipes/column-transform-pipe';
import { DialogFormComponent } from '@shared/dialogs/dialog-form/dialog-form.component';
import { SubmissionStepFaqComponent } from '@shared/components/submission-step-faq/submission-step-faq.component';
import { ParcelInfoComponent } from '@feature/site/parcel-info/parcel-info.component';

// Material Modules
const materialModules = [
  MatAutocompleteModule,
  MatBadgeModule,
  MatButtonModule,
  MatButtonToggleModule,
  MatCardModule,
  MatCheckboxModule,
  MatChipsModule,
  MatDatepickerModule,
  MatDialogModule,
  MatDividerModule,
  MatExpansionModule,
  MatTabGroup,
  MatTabLabel,
  MatTab,
  MatFormFieldModule,
  MatIconModule,
  MatInputModule,
  MatMenuModule,
  MatNativeDateModule,
  MatProgressSpinnerModule,
  MatRadioModule,
  MatSelectModule,
  MatSidenavModule,
  MatSliderModule,
  MatSlideToggleModule,
  MatSnackBarModule,
  MatStepperModule,
  MatSortModule,
  MatTableModule,
  DragDropModule,
  MatPaginatorModule,
  MatToolbarModule,
  MatTooltipModule,
];

const sharedComponents = [
  DisplayTableFilterComponent,
  DisplayTableBaseComponent,
  AppHeaderComponent,
  ClickDirective,
  CurrencyMaskDirective,
  DashboardBannerComponent,
  DateMaskDirective,
  DisturbanceReportComponent,
  DragDropUploadDirective,
  EmailFormErrorComponent,
  EnvRibbonComponent,
  FCSortIconComponent,
  FormUnloadWarningDirective,
  HtmlTooltipDirective,
  PaginatorLabelDirective,
  LegalPageComponent,
  NewSiteStepOneComponent,
  ParcelInfoComponent,
  PeriodicReportComponent,
  ReportResponseComponent,
  ReportFormComponent,
  ProfilePageComponent,
  ProgramListComponent,
  ProgressBarComponent,
  RegexFilterDirective,
  SiteCardComponent,
  SiteDetailInformationComponent,
  SiteDetailOverviewComponent,
  SiteDetailNotesComponent,
  SiteDetailPaymentsComponent,
  SiteDetailReportsComponent,
  SiteDocumentListComponent,
  SubmissionStepFaqComponent,
  SiteVerificationVisitsComponent,
  SpinnerComponent,
  ToastComponent,
  TablePaginationComponent,
  StringDiffComponent,
  UserActivityDashboardComponent,
  VerifyAccountComponent,
  AppFooterComponent
];

const formComponents = [FCControlComponent, FCDatepickerComponent];

const sharedDialogComponents = [
  AddDocusignDialogComponent,
  AddUserDialogComponent,
  AdjustValueDialogComponent,
  ConfirmRemovalDialogComponent,
  DialogChangeActingAsUserComponent,
  DialogChangeApplicantComponent,
  DialogChangeDashboardNameComponent,
  DialogEditCountyDataComponent,
  DialogEditSiteNoteComponent,
  DialogEditPaymentPeriodComponent,
  DialogEditCustomOfferComponent,
  DialogEditReportingPeriodComponent,
  DialogFormComponent,
  DialogChangeSiteStateComponent,
  DialogComingSoonComponent,
  DialogConfirmPaymentComponent,
  DialogDownForMaintenanceComponent,
  DialogEligibilityQuestionComponent,
  DialogImportKmlComponent,
  DialogRetainSubmitDateComponent,
  DialogSelectOptionComponent,
  DialogManualIDComponent,
  DialogOutOfRegionComponent,
  DialogResubmitNoteComponent,
  DialogUploadDocComponent,
  DialogYesNoComponent,
  OtpDialogComponent,
  DialogSplitSiteComponent,
  DialogSendEdocComponent,
];

const sharedModules = [
  CKEditorModule,
  CommonModule,
  NgxChartsModule,
  FontAwesomeModule,
  FormsModule,
  OverlayModule,
  PortalModule,
  ReactiveFormsModule,
  RouterModule,
];

const sharedPipes = [ColumnTransformPipe]

@NgModule({
  imports: [...materialModules, ...sharedModules, ...sharedPipes],
  declarations: [
    ...formComponents,
    ...sharedComponents,
    ...sharedDialogComponents
  ],
  exports: [...formComponents, ...materialModules, ...sharedComponents, ...sharedDialogComponents, ...sharedModules, ...sharedPipes],
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'outline',
        floatLabel: 'never',
        subscriptSizing: 'dynamic',
      },
    },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        duration: 4000,
      },
    },
    {
      multi: true,
      provide: NG_VALIDATORS,
      useExisting: FCControlComponent,
    },
  ],
})
export class SharedModule {
  constructor(library: FaIconLibrary, faConfig: FaConfig) {
    faConfig.fixedWidth = true;

    /* Add all font awesome icons here, and you won't need to import them in individual components */
    /* NOTE: Alphabetize items within their sections if you can, please! */

    /* Regular Icons */
    library.addIcons(faArrowLeft);
    library.addIcons(faArrowsRotate);
    library.addIcons(farArrowUpRightFromSquare);
    library.addIcons(faBadgeDollar);
    library.addIcons(faCrosshairs);
    library.addIcons(faCircleCheck);
    library.addIcons(faCircleInfo);
    library.addIcons(faCircleQuestion);
    library.addIcons(faClone);
    library.addIcons(faDownload);
    library.addIcons(farEdit);
    library.addIcons(farEye);
    library.addIcons(farEyeSlash);
    library.addIcons(faFlag);
    library.addIcons(faFilter);
    library.addIcons(faLock);
    library.addIcons(farMap);
    library.addIcons(faPaperclip);
    library.addIcons(faPencil);
    library.addIcons(faRoad);
    library.addIcons(faShare);
    library.addIcons(faSignsPost);
    library.addIcons(faSquareDashed);
    library.addIcons(faSquareDashedCirclePlus);
    library.addIcons(faTrash);
    library.addIcons(faTrashUndo);
    library.addIcons(farTree);
    library.addIcons(farTreeDeciduous);
    library.addIcons(faTriangleExclamation);
    library.addIcons(faUpload);
    library.addIcons(faUserShield);
    library.addIcons(faWater);
    library.addIcons(faXmark);

    /* Solid Icons */
    library.addIcons(faArrowUpRightFromSquare);
    library.addIcons(faBars);
    library.addIcons(faBorderAll);
    library.addIcons(faCaretDown);
    library.addIcons(faCaretUp);
    library.addIcons(faChartSimple);
    library.addIcons(faCheck);
    library.addIcons(faCheckCircle);
    library.addIcons(faChevronLeft);
    library.addIcons(faChevronRight);
    library.addIcons(faChevronsDown);
    library.addIcons(faChevronsLeft);
    library.addIcons(faChevronsRight);
    library.addIcons(faChevronsUp);
    library.addIcons(faCircle);
    library.addIcons(fasCircleCheck);
    library.addIcons(faCircleExclamation);
    library.addIcons(faCircleMinus);
    library.addIcons(faCirclePlus);
    library.addIcons(faCircleUser);
    library.addIcons(faCircleXmark);
    library.addIcons(faEdit);
    library.addIcons(faExpandWide);
    library.addIcons(faEye);
    library.addIcons(faEyeSlash);
    library.addIcons(faGauge);
    library.addIcons(faMinus);
    library.addIcons(faMoneyCheckDollar);
    library.addIcons(faOctagon);
    library.addIcons(faPaperPlane);
    library.addIcons(faPenToSquare);
    library.addIcons(faPlus);
    library.addIcons(faQuestion);
    library.addIcons(faQuestionCircle);
    library.addIcons(faRoad);
    library.addIcons(faRotateLeft);
    library.addIcons(faShieldHalved);
    library.addIcons(faSolidMap);
    library.addIcons(faSort);
    library.addIcons(faSortDown);
    library.addIcons(faSortUp);
    library.addIcons(faStar);
    library.addIcons(faTableColumns);
    library.addIcons(faTree);
    library.addIcons(faTreeDeciduous);
    library.addIcons(faTriangle);
    library.addIcons(faWater);

    /* Light Icons */
    library.addIcons(faArrowPointer);
    library.addIcons(faArrowsUpDownLeftRight);
    library.addIcons(faClockRotateLeft);
    library.addIcons(faDrawPolygon);
    library.addIcons(faDrawSquare);
    library.addIcons(faEnvelope);
    library.addIcons(faFile);
    library.addIcons(faFileDownload);
    library.addIcons(faFileUpload);
    library.addIcons(faFloppyDisk);
    library.addIcons(faHandPointer);
    library.addIcons(faLayerGroup);
    library.addIcons(faLayerMinus);
    library.addIcons(faLayerPlus);
    library.addIcons(faMap);
    library.addIcons(faPenNib);
    library.addIcons(faPhone);
    library.addIcons(faEnvelope);
    library.addIcons(falRotateLeft);

    /* Custom Icons */
    library.addIcons(facCustomTree);
    library.addIcons(faDownloadMap);
    library.addIcons(faUploadMap);
  }
}
